import React, { useState, useEffect } from 'react';
import { getServices, deleteService, toggleServiceStatus, getCategories } from '../../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ServiceForm from '../forms/ServiceForm';
import toast from 'react-hot-toast';

const ServicesModule = () => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    fetchServices();
    fetchCategories();
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await getServices();
      const servicesData = response.data || response || [];
      setServices(servicesData);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Error fetching services');
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      let categoriesArray = [];
      
      if (response && response.data && Array.isArray(response.data)) {
        categoriesArray = response.data.filter(cat => cat.type === 'service');
      } else if (response && Array.isArray(response)) {
        categoriesArray = response.filter(cat => cat.type === 'service');
      }
      
      setCategories(categoriesArray);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await deleteService(id);
        toast.success('Service deleted successfully');
        fetchServices();
      } catch (error) {
        toast.error('Error deleting service');
      }
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await toggleServiceStatus(id);
      toast.success(`Service ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchServices();
    } catch (error) {
      toast.error('Error toggling service status');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  const getCategoryIcon = (categoryName) => {
    const category = categories.find(c => c.name === categoryName);
    return category?.icon || '⚙️';
  };

  const filteredServices = filterCategory === 'all' 
    ? services 
    : services.filter(service => service.category === filterCategory);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ color: '#0A192F', fontSize: '1.3rem', marginBottom: '0.25rem' }}>Services</h2>
          <p style={{ color: '#5A6E7F', fontSize: '0.875rem' }}>
            <i className="fas fa-chart-line"></i> Total Services: {services.length}
          </p>
        </div>
        
        {/* Category Filter */}
        {categories.length > 0 && (
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              background: 'white'
            }}
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat._id} value={cat.name}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>
        )}
        
        <button 
          onClick={() => { setEditingItem(null); setShowModal(true); }} 
          style={{ padding: '0.5rem 1rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <i className="fas fa-plus" /> Add New Service
        </button>
      </div>

      {services.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', background: '#F7F9FC', borderRadius: '12px' }}>
          <i className="fas fa-cogs" style={{ fontSize: '3rem', color: '#CBD5E1', marginBottom: '1rem' }} />
          <p style={{ color: '#5A6E7F' }}>No services found. Click "Add New Service" to create one.</p>
          <p style={{ color: '#94A3B8', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            <i className="fas fa-info-circle"></i> Make sure you have service categories created first.
          </p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead style={{ background: '#F7F9FC' }}>
              <tr>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Image</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Category</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Price</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredServices.map((service) => (
                <tr key={service._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                  <td style={{ padding: '1rem' }}>
                    {service.imageUrl ? (
                      <img 
                        src={getFullImageUrl(service.imageUrl)} 
                        alt={service.name} 
                        style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '8px' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div style={{ width: '40px', height: '40px', background: '#F7F9FC', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="fas fa-cogs" style={{ color: '#CBD5E1' }} />
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '1rem', fontWeight: 500 }}>{service.name}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      background: '#E3F2FD',
                      padding: '0.2rem 0.6rem',
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      color: '#2C7A7B'
                    }}>
                      <span>{getCategoryIcon(service.category)}</span>
                      {service.category}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', fontWeight: 600, color: '#2C7A7B' }}>{service.price || 'Contact Us'}</td>
                  <td style={{ padding: '1rem' }}>
                    <button 
                      onClick={() => handleToggle(service._id, service.isActive)} 
                      style={{ padding: '0.25rem 0.75rem', borderRadius: '20px', border: 'none', background: service.isActive ? '#D4EDDA' : '#F8D7DA', color: service.isActive ? '#155724' : '#721C24', cursor: 'pointer', fontSize: '0.75rem' }}
                    >
                      {service.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button 
                      onClick={() => handleEdit(service)} 
                      style={{ padding: '0.25rem 0.75rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.5rem' }}
                    >
                      <i className="fas fa-edit" /> Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(service._id)} 
                      style={{ padding: '0.25rem 0.75rem', background: '#DC2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      <i className="fas fa-trash" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Category Info Box */}
      {categories.length === 0 && !loading && (
        <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#FFF4E6', borderRadius: '8px', borderLeft: '4px solid #F5A623' }}>
          <p style={{ color: '#5A6E7F', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <i className="fas fa-info-circle" style={{ color: '#F5A623' }}></i>
            No service categories found. Please add service categories in the Category Manager first.
          </p>
        </div>
      )}

      {showModal && (
        <ServiceForm 
          editingItem={editingItem} 
          onClose={handleCloseModal} 
          onRefresh={fetchServices} 
        />
      )}
    </div>
  );
};

export default ServicesModule;